<template>
  <BaseModal
    :is-open="isOpen"
    title="Idle Warning"
    max-width="500px"
    :closable="false"
  >
    <div class="idle-warning-container">
      <!-- Warning Icon -->
      <div class="warning-icon">
        <span class="material-symbols-outlined">schedule</span>
      </div>

      <!-- Warning Message -->
      <div class="warning-message">
        <h3 class="message-title">You have been idle!</h3>
        <p class="message-text">{{ warningMessage }}</p>
      </div>

      <!-- Countdown Timer -->
      <div class="countdown-section">
        <div class="countdown-display">
          <span class="countdown-label">Time Remaining:</span>
          <span class="countdown-timer">{{ formatTime(timeRemaining) }}</span>
        </div>
        <div class="countdown-bar-container">
          <div
            class="countdown-bar"
            :style="{ width: `${timerProgress}%` }"
            :class="{ critical: timeRemaining <= 10 }"
          ></div>
        </div>
      </div>

      <!-- Action Info -->
      <div class="action-info">
        <div class="info-item">
          <span class="material-symbols-outlined">info</span>
          <span>Take your turn now to prevent automatic bot replacement</span>
        </div>
      </div>

      <!-- Acknowledge Button -->
      <button @click="handleAcknowledge" class="btn-acknowledge">
        <span class="material-symbols-outlined">check_circle</span>
        <span>I'm Here - Continue Playing</span>
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * IdleWarningModal Component
 *
 * Warns player they have been idle and will be replaced by a bot.
 * Shows countdown timer for how long until replacement.
 *
 * Props:
 * - message: Warning message text
 * - time_remaining: Time in seconds before replacement
 * - total_time: Total timeout duration (for progress bar)
 *
 * Emits:
 * - acknowledge: [] - Player acknowledged warning
 * - timeout: [] - Timer expired
 */

interface Props {
  isOpen: boolean
  data: {
    message?: string
    time_remaining?: number // seconds
    total_time?: number // seconds
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  acknowledge: []
  timeout: []
  close: []
}>()

/**
 * Warning message
 */
const warningMessage = computed(() => {
  return (
    props.data?.message ||
    'You have been inactive during your turn. Please take an action or you may be replaced with a bot.'
  )
})

/**
 * Time remaining (reactive countdown)
 */
const timeRemaining = ref(props.data?.time_remaining || 60)
const totalTime = computed(() => props.data?.total_time || props.data?.time_remaining || 60)
const timerInterval = ref<number | null>(null)

/**
 * Timer progress percentage
 */
const timerProgress = computed(() => {
  return (timeRemaining.value / totalTime.value) * 100
})

/**
 * Start countdown timer
 */
function startTimer(): void {
  if (timerInterval.value) clearInterval(timerInterval.value)

  timerInterval.value = window.setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value -= 1
    } else {
      if (timerInterval.value) clearInterval(timerInterval.value)
      emit('timeout')
      emit('close')
    }
  }, 1000)
}

/**
 * Stop timer
 */
function stopTimer(): void {
  if (timerInterval.value) {
    clearInterval(timerInterval.value)
    timerInterval.value = null
  }
}

/**
 * Watch for modal open to start timer
 */
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      timeRemaining.value = props.data?.time_remaining || 60
      startTimer()
    } else {
      stopTimer()
    }
  },
  { immediate: true }
)

/**
 * Cleanup timer on unmount
 */
onUnmounted(() => {
  stopTimer()
})

/**
 * Format time (MM:SS)
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Handle acknowledge
 */
function handleAcknowledge(): void {
  stopTimer()
  emit('acknowledge')
  emit('close')
}
</script>

<style scoped>
.idle-warning-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Warning Icon */
.warning-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(245, 158, 11, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse-warning 2s ease-in-out infinite;
}

.warning-icon .material-symbols-outlined {
  font-size: 3rem;
  color: #fbbf24;
}

@keyframes pulse-warning {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(251, 191, 36, 0);
  }
}

/* Warning Message */
.warning-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
}

.message-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #fbbf24;
  margin: 0;
}

.message-text {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin: 0;
  max-width: 400px;
}

/* Countdown Section */
.countdown-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(251, 191, 36, 0.1);
  border: 2px solid rgba(251, 191, 36, 0.3);
  border-radius: 12px;
}

.countdown-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.countdown-label {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.countdown-timer {
  font-size: 3rem;
  font-weight: 700;
  color: #fbbf24;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.countdown-bar-container {
  width: 100%;
  height: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  overflow: hidden;
}

.countdown-bar {
  height: 100%;
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
  border-radius: 6px;
  transition: width 1s linear;
}

.countdown-bar.critical {
  background: linear-gradient(90deg, #ef4444, #dc2626);
  animation: blink 0.5s ease-in-out infinite;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Action Info */
.action-info {
  width: 100%;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  font-size: 0.9375rem;
  color: rgba(147, 197, 253, 0.9);
  line-height: 1.5;
}

.info-item .material-symbols-outlined {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Acknowledge Button */
.btn-acknowledge {
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  margin-top: 0.5rem;
}

.btn-acknowledge:hover {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  transform: translateY(-2px);
}

.btn-acknowledge:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.btn-acknowledge .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Responsive */
@media (max-width: 640px) {
  .warning-icon {
    width: 64px;
    height: 64px;
  }

  .warning-icon .material-symbols-outlined {
    font-size: 2.5rem;
  }

  .message-title {
    font-size: 1.125rem;
  }

  .message-text {
    font-size: 0.9375rem;
  }

  .countdown-timer {
    font-size: 2.5rem;
  }

  .countdown-section {
    padding: 1.25rem;
  }
}
</style>
